import {useMemo} from 'react';

import {useProductSearch} from '@salesforce/commerce-sdk-react';
import {useProducts} from '@amblique/king-living/app/hooks/use-products';

import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';

/** Max products when feed is resolved from a category (PLP-aligned search). */
const CAROUSEL_CATEGORY_PRODUCT_LIMIT = 24;

/**
 * Normalises SCAPI shopperProducts entries (stable `productId` for keys and maps).
 *
 * @param {object} product
 * @returns {object}
 */
function normalizeProductForTile(product) {
  if (!product) return product;
  if (product.productId) return product;

  return {...product, productId: product.id};
}

/**
 * @param {object[]} list - SCAPI shopperProducts `data` array
 * @param {string[]} orderedIds - Desired order (Contentful IDs or search hit order)
 * @returns {object[]}
 */
function mapProductsByOrderedIds(list, orderedIds) {
  const byId = new Map(
    list.map((p) => {
      const key = String(p.id || p.productId);

      return [key, normalizeProductForTile(p)];
    })
  );

  return orderedIds.map((id) => byId.get(String(id))).filter(Boolean);
}

/**
 * Resolves products for the Contentful ProductTiles block. Product IDs take precedence
 * over category search.
 *
 * @param {object} args
 * @param {string[]|null|undefined} args.productIds
 * @param {string|null|undefined} args.categoryId
 * @returns {{products: object[], isLoading: boolean}}
 */
export function useContentfulCarouselProducts({productIds, categoryId}) {
  const normalizedIds = useMemo(() => {
    return (Array.isArray(productIds) ? productIds : [])
      .map((id) => (typeof id === 'string' ? id.trim() : String(id)))
      .filter(Boolean);
  }, [productIds]);

  const trimmedCategoryId = typeof categoryId === 'string' ? categoryId.trim() : '';
  const feedFromIds = normalizedIds.length > 0;
  const feedFromCategory = !feedFromIds && Boolean(trimmedCategoryId);

  const {data: productsData, isLoading: isLoadingProducts} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        // `variations` is required so SCAPI returns `variationAttributes` on each product,
        // which RangeProductTile reads via getCoverSwatches() to render cover swatches.
        expand: [...GET_PRODUCTS_PARAMS.expand, 'variations'],
        ids: feedFromIds ? normalizedIds.join(',') : '',
      },
    },
    {enabled: feedFromIds, staleTime: 5 * 60 * 1000}
  );

  const searchParameters = useMemo(
    () => ({
      refine: [`cgid=${trimmedCategoryId}`],
      limit: CAROUSEL_CATEGORY_PRODUCT_LIMIT,
      offset: 0,
      perPricebook: true,
      allVariationProperties: true,
      allImages: true,
      expand: [
        'promotions',
        'variations',
        'prices',
        'images',
        'page_meta_tags',
        'custom_properties'
      ],
    }),
    [trimmedCategoryId]
  );

  const {data: searchResult, isLoading: isLoadingSearch} = useProductSearch(
    {parameters: searchParameters},
    {enabled: feedFromCategory, keepPreviousData: true}
  );

  const categoryHitIds = useMemo(() => {
    if (!feedFromCategory || !searchResult?.hits?.length) return [];

    return searchResult.hits
      .map((h) => h.productId)
      .filter(Boolean)
      .slice(0, CAROUSEL_CATEGORY_PRODUCT_LIMIT);
  }, [feedFromCategory, searchResult]);

  const {data: categoryProductsData, isLoading: isLoadingCategoryProducts} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        expand: [...GET_PRODUCTS_PARAMS.expand, 'variations'],
        ids: feedFromCategory && categoryHitIds.length ? categoryHitIds.join(',') : '',
      },
    },
    {
      enabled: feedFromCategory && categoryHitIds.length > 0,
      staleTime: 5 * 60 * 1000,
    }
  );

  const products = useMemo(() => {
    if (feedFromIds) {
      return mapProductsByOrderedIds(productsData?.data || [], normalizedIds);
    }

    if (feedFromCategory) {
      return mapProductsByOrderedIds(categoryProductsData?.data || [], categoryHitIds);
    }

    return [];
  }, [
    feedFromIds,
    feedFromCategory,
    productsData,
    categoryProductsData,
    categoryHitIds,
    normalizedIds
  ]);

  const isLoading = feedFromIds ?
    isLoadingProducts :
    feedFromCategory ?
      isLoadingSearch || isLoadingCategoryProducts :
      false;

  return {products, isLoading};
}
